import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, MenuItem, Radio, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import { FichaContenedor, FilaFicha } from "../../solicitudCheque/components/FichaSolicitud";
import { buscarEgresosOC, enlazarPago, fmt, mensajeError } from "../services/ordenCompraService";

const vacio = () => ({ num_factura: "", tipo: "Proveedores", forma_pago: "Efectivo", moneda: "Bolivianos", monto: "", costo_envio: "" });
const FILAS = 6;

const headSx = { fontWeight: 800, fontSize: 11.5, whiteSpace: "nowrap", bgcolor: "action.hover" };
const celda = { py: 0.5, px: 1, fontSize: 12.5, verticalAlign: "middle" };
const corta = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

// Enlaza a la orden un egreso (cheque) ya emitido: 1) se busca y elige el egreso, 2) se completan los datos del pago.
export default function EnlazarPagoDialog({ open, ordenId, catalogos, totales, onClose, onGuardado }) {
  const [buscar, setBuscar] = useState("");
  const [q, setQ] = useState("");
  const [pagina, setPagina] = useState(0);
  const [datos, setDatos] = useState({ rows: [], total: 0 });
  const [cargando, setCargando] = useState(false);
  const [sel, setSel] = useState(null); // egreso elegido (fila completa, para seguir visible al cambiar de página)
  const [form, setForm] = useState(vacio());
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [paso, setPaso] = useState("form"); // form | resumen

  useEffect(() => {
    if (!open) return;
    setPaso("form");
    setBuscar(""); setQ(""); setPagina(0); setSel(null); setForm(vacio()); setError("");
  }, [open]);

  useEffect(() => {
    const tm = setTimeout(() => { setQ(buscar.trim()); setPagina(0); }, 300);
    return () => clearTimeout(tm);
  }, [buscar]);

  const cargar = useCallback(async () => {
    if (!open) return;
    setCargando(true);
    try {
      const { data } = await buscarEgresosOC({ q: q || undefined, page: pagina + 1, limit: FILAS, orden: ordenId });
      setDatos(data);
    } catch (e) {
      setError(mensajeError(e, "No se pudieron cargar los egresos."));
    } finally {
      setCargando(false);
    }
  }, [open, q, pagina, ordenId]);
  useEffect(() => { cargar(); }, [cargar]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const simMoneda = form.moneda === "Dolares" ? "$us" : "Bs";

  const revisar = (e) => {
    e.preventDefault();
    if (!sel) return setError("Elige el egreso que quieres enlazar.");
    setError("");
    setPaso("resumen");
  };

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      await enlazarPago(ordenId, { ...form, cod_egreso: sel.id });
      onGuardado();
    } catch (err) {
      setError(mensajeError(err, "No se pudo enlazar el pago."));
      setPaso("form");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="md" fullWidth component="form" onSubmit={revisar} scroll="paper">
      <DialogTitle sx={{ py: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Typography component="span" sx={{ fontSize: 18, fontWeight: 800 }}>{paso === "form" ? "Agregar pago emitido" : "Confirma el pago a enlazar"}</Typography>
          <Box sx={{ display: "flex", gap: 0.75 }} aria-label="Paso">
            <Chip size="small" label="1 · Datos" color={paso === "form" ? "primary" : "default"} variant={paso === "form" ? "filled" : "outlined"} />
            <Chip size="small" label="2 · Confirmar" color={paso === "resumen" ? "primary" : "default"} variant={paso === "resumen" ? "filled" : "outlined"} />
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 400 }}>{paso === "form" ? "Enlaza a esta orden un cheque que ya se emitió en Egresos." : "Revisa lo que se va a registrar antes de enlazar."}</Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "background.default", p: 1.5, display: "grid", gap: 1 }}>
        {paso === "resumen" && sel && (
          <>
            <FichaContenedor>
              <FilaFicha campos={[{ etiqueta: "Orden de compra", valor: `N.º ${ordenId}` }, { etiqueta: "Egreso", valor: `N.º ${sel.id} · ${sel.fecha}` }]} />
              <FilaFicha campos={[{ etiqueta: "Dirigido a", valor: sel.nombre }]} />
              <FilaFicha campos={[{ etiqueta: "Concepto", valor: sel.concepto }]} />
              <FilaFicha campos={[{ etiqueta: "N.º de cheque", valor: sel.nocheque }, { etiqueta: "Banco", valor: sel.banco }]} />
              <FilaFicha campos={[{ etiqueta: "Importe en Bs", valor: fmt(sel.bolivianos) }, { etiqueta: "Importe en $us", valor: fmt(sel.dolares) }]} />
            </FichaContenedor>
            <FichaContenedor>
              <FilaFicha campos={[{ etiqueta: "N.º de factura", valor: form.num_factura }, { etiqueta: "Tipo", valor: form.tipo }]} />
              <FilaFicha campos={[{ etiqueta: "Forma de pago", valor: form.forma_pago }, { etiqueta: "Moneda factura", valor: form.moneda === "Dolares" ? "Dólares" : "Bolivianos" }]} />
              <FilaFicha campos={[{ etiqueta: "Monto factura", valor: form.monto ? `${simMoneda} ${form.monto}` : "" }, { etiqueta: "Costo del envío", valor: form.costo_envio ? `${simMoneda} ${form.costo_envio}` : "" }]} />
            </FichaContenedor>
            {totales && (
              <Alert severity="info" sx={{ py: 0.25 }}>
                Total pagado de la orden: Bs {fmt(totales.pagado_bs)} → <b>Bs {fmt(totales.pagado_bs + sel.bolivianos)}</b> ({totales.bolivianos > 0 ? Math.round(((totales.pagado_bs + sel.bolivianos) / totales.bolivianos) * 100) : 0}% del total de la compra). Este egreso también cambia el costo unitario real de las líneas.
              </Alert>
            )}
          </>
        )}

        {paso === "form" && (
          <>
            <Box sx={{ p: 1.5, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1.5, display: "grid", gap: 1 }}>
              <TextField
                fullWidth size="small" hiddenLabel placeholder="1. Elige el egreso: busca por código, destinatario, concepto o N.º de cheque…" value={buscar} onChange={(e) => setBuscar(e.target.value)}
                slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>, endAdornment: cargando ? <CircularProgress size={16} /> : null }, htmlInput: { "aria-label": "Buscar egreso" } }}
              />
              <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ ...headSx, width: 38 }} />
                      <TableCell sx={{ ...headSx, width: 96 }}>Egreso</TableCell>
                      <TableCell sx={headSx}>Dirigido a / Concepto</TableCell>
                      <TableCell sx={{ ...headSx, width: 112 }}>Cheque</TableCell>
                      <TableCell sx={{ ...headSx, width: 108 }} align="right">Importe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!datos.rows.length ? (
                      <TableRow><TableCell colSpan={5} sx={{ ...celda, py: 3, textAlign: "center", color: "text.secondary" }}>{cargando ? "Cargando…" : "No hay egresos con esa búsqueda."}</TableCell></TableRow>
                    ) : datos.rows.map((e) => {
                      const bloqueado = e.en_esta_orden;
                      return (
                        <TableRow key={e.id} hover={!bloqueado} selected={sel?.id === e.id} onClick={() => !bloqueado && setSel(e)} sx={{ cursor: bloqueado ? "default" : "pointer", opacity: bloqueado ? 0.55 : 1 }}>
                          <TableCell padding="checkbox"><Radio size="small" checked={sel?.id === e.id} disabled={bloqueado} onChange={() => setSel(e)} inputProps={{ "aria-label": `Elegir egreso ${e.id}` }} /></TableCell>
                          <TableCell sx={celda}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 800, lineHeight: 1.2 }}>{e.id}</Typography>
                            <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.2 }}>{e.fecha}</Typography>
                          </TableCell>
                          <TableCell sx={{ ...celda, minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                              <Tooltip title={e.nombre}><Box sx={{ ...corta, fontWeight: 600, flexShrink: 1 }}>{e.nombre}</Box></Tooltip>
                              {bloqueado && <Chip size="small" color="success" variant="outlined" label="Ya enlazado" sx={{ height: 18, fontSize: 10.5, flexShrink: 0 }} />}
                              {!bloqueado && e.en_otras_ordenes.length > 0 && <Chip size="small" color="warning" variant="outlined" label={`En OC ${e.en_otras_ordenes.join(", ")}`} sx={{ height: 18, fontSize: 10.5, flexShrink: 0 }} />}
                            </Box>
                            <Tooltip title={e.concepto}><Box sx={{ ...corta, fontSize: 11, color: "text.secondary" }}>{e.concepto}</Box></Tooltip>
                          </TableCell>
                          <TableCell sx={{ ...celda, ...corta }}>{e.nocheque}</TableCell>
                          <TableCell sx={{ ...celda, fontVariantNumeric: "tabular-nums" }} align="right">
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.2 }}>{fmt(e.bolivianos)}</Typography>
                            <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.2 }}>$us {fmt(e.dolares)}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div" count={datos.total} page={pagina} rowsPerPage={FILAS} rowsPerPageOptions={[]} onPageChange={(_, p) => setPagina(p)}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`} sx={{ minHeight: 36, "& .MuiToolbar-root": { minHeight: 36 } }}
              />
              {sel && (
                <Alert icon={<CheckCircleIcon fontSize="small" />} severity="success" sx={{ py: 0 }}>
                  Egreso <b>{sel.id}</b> · {sel.nombre} · Bs {fmt(sel.bolivianos)} / $us {fmt(sel.dolares)}
                </Alert>
              )}
            </Box>

            <Box sx={{ opacity: sel ? 1 : 0.6 }}>
              <FichaContenedor>
                <FilaFicha campos={[
                  { etiqueta: "N.º de factura", control: <TextField fullWidth size="small" hiddenLabel value={form.num_factura} onChange={set("num_factura")} disabled={!sel} slotProps={{ htmlInput: { "aria-label": "N.º de factura", ...{} } }} /> },
                  { etiqueta: "Tipo", control: (
                    <TextField fullWidth size="small" select hiddenLabel value={form.tipo} onChange={set("tipo")} disabled={!sel} slotProps={{ htmlInput: { "aria-label": "Tipo" } }}>
                      {(catalogos?.tipos_pago || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                    </TextField>
                  ) },
                ]}
                />
                <FilaFicha campos={[
                  { etiqueta: "Forma de pago", control: (
                    <TextField fullWidth size="small" select hiddenLabel value={form.forma_pago} onChange={set("forma_pago")} disabled={!sel} slotProps={{ htmlInput: { "aria-label": "Forma de pago" } }}>
                      {(catalogos?.formas_pago || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                    </TextField>
                  ) },
                  { etiqueta: "Moneda factura", control: (
                    <TextField fullWidth size="small" select hiddenLabel value={form.moneda} onChange={set("moneda")} disabled={!sel} slotProps={{ htmlInput: { "aria-label": "Moneda de la factura" } }}>
                      <MenuItem value="Bolivianos">Bolivianos</MenuItem>
                      <MenuItem value="Dolares">Dólares</MenuItem>
                    </TextField>
                  ) },
                ]}
                />
                <FilaFicha campos={[
                  { etiqueta: "Monto factura", control: (
                    <TextField
                      fullWidth size="small" hiddenLabel value={form.monto} onChange={set("monto")} disabled={!sel} placeholder="0.00"
                      slotProps={{ input: { startAdornment: <InputAdornment position="start">{simMoneda}</InputAdornment> }, htmlInput: { inputMode: "decimal", "aria-label": "Monto de la factura" } }}
                    />
                  ) },
                  { etiqueta: "Costo del envío", control: (
                    <TextField
                      fullWidth size="small" hiddenLabel value={form.costo_envio} onChange={set("costo_envio")} disabled={!sel} placeholder="0.00"
                      slotProps={{ input: { startAdornment: <InputAdornment position="start">{simMoneda}</InputAdornment> }, htmlInput: { inputMode: "decimal", "aria-label": "Costo del envío" } }}
                    />
                  ) },
                ]}
                />
              </FichaContenedor>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {paso === "form" ? (
          <>
            <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={!sel} sx={{ textTransform: "none", fontWeight: 700 }}>Revisar antes de enlazar</Button>
          </>
        ) : (
          <>
            <Button onClick={() => setPaso("form")} disabled={guardando} sx={{ textTransform: "none" }}>Volver y editar</Button>
            <Button variant="contained" onClick={guardar} disabled={guardando} sx={{ textTransform: "none", fontWeight: 700 }}>
              {guardando ? <CircularProgress size={20} color="inherit" /> : `Confirmar y enlazar egreso ${sel?.id}`}
            </Button>
          </>
        )}
      </DialogActions>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={(_, m) => m !== "clickaway" && setError("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ width: "100%" }}>{error}</Alert>
      </Snackbar>
    </Dialog>
  );
}
